import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { deleteAccount } from "../api/auth";
import {
  DELETE_CONFIRMATION_PHRASE,
  canConfirmDelete,
  runDeleteConfirm,
  type DeletePhase,
} from "../lib/deleteAccountFlow";

// Stable URL: linked from privacy.html ("the account page at
// account.prismstreaming.com") and is the exact URL that goes in Google
// Play's Data Safety "account deletion" field / what Apple reviewers look
// for alongside the in-app iOS flow. Don't rename this route without
// updating both.
export function DeleteAccountPage() {
  const { api, clearSession } = useAuth();
  const nav = useNavigate();
  const [typed, setTyped] = useState("");
  const [phase, setPhase] = useState<DeletePhase>({ status: "editing" });

  const busy = phase.status === "deleting";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !canConfirmDelete(typed)) return;
    setPhase({ status: "deleting" });
    const result = await runDeleteConfirm(typed, async () => {
      await deleteAccount(api);
    });
    setPhase(result);
    if (result.status === "deleted") {
      // Terminal state lives on its own unauthenticated route
      // (/account-deleted) rather than being rendered inline here — this
      // page is wrapped in RequireAuth, and clearSession() flips auth
      // state to unauthenticated, which would otherwise force-navigate to
      // /login out from under an inline "deleted" message before the user
      // ever sees it.
      clearSession();
      nav("/account-deleted", { replace: true });
    }
  }

  return (
    <div className="vstack">
      <div>
        <h1>Delete account</h1>
        <p className="meta">This permanently deletes your Prism account. It can't be undone.</p>
      </div>

      <form className="card" onSubmit={submit}>
        <p>
          Deleting your account removes your Prism account, every device paired with it, and every
          server pairing tied to it.
        </p>
        <p className="meta">
          Media stored on any Prism server is <strong>not</strong> touched by this — it belongs to
          that server's owner, not to your account.
        </p>
        <div className="field">
          <label htmlFor="confirm">Type {DELETE_CONFIRMATION_PHRASE} to confirm</label>
          <input
            id="confirm"
            type="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            value={typed}
            disabled={busy}
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
        {phase.status === "failed" && <div className="error">{phase.message}</div>}
        <button className="btn danger" type="submit" disabled={busy || !canConfirmDelete(typed)}>
          {busy ? "Deleting…" : "Delete account"}
        </button>
      </form>
    </div>
  );
}
